import { useCallback } from 'react';
import { useLockedEntry } from '@/hooks/useLockedEntry';
import type { EntitlementFeatureKey } from '@/types/package';

/** Nav item ids whose page is plan-gated. They stay in the nav when locked (lock badge, locked page on open). */
export type GatedNavId = 'brand-check' | 'blog' | 'analytics';

export interface NavGate {
  /** Not shown at all: package still loading, or (analytics only) the base switch is off and the hub is not locked */
  hidden: boolean;
  /** Really blocked for this brand right now (same resolver as the request gates, mode included) */
  locked: boolean;
  /** Entitlement key behind the lock, for the data-testid */
  feature: EntitlementFeatureKey;
}

/**
 * One decision for RailSidebar and BottomNav so desktop and mobile never disagree.
 * Returns null for ids that are not plan-gated.
 */
export const useNavGating = (): ((id: string) => NavGate | null) => {
  const brandCheck = useLockedEntry('brand_check');
  const wordpress = useLockedEntry('wordpress');
  const analyticsHub = useLockedEntry('analytics_hub');
  const analyticsBase = useLockedEntry('analytics');

  const brandCheckState = { decided: brandCheck.decided, locked: brandCheck.locked };
  const wordpressState = { decided: wordpress.decided, locked: wordpress.locked };
  // Analytics is unusable when the base switch is off OR the hub is locked (enforce): both show locked
  const analyticsDecided = analyticsBase.decided && analyticsHub.decided;
  const analyticsLocked = analyticsBase.locked || analyticsHub.locked;

  return useCallback(
    (id: string): NavGate | null => {
      if (id === 'brand-check') {
        return { hidden: !brandCheckState.decided, locked: brandCheckState.locked, feature: 'brand_check' };
      }
      if (id === 'blog') {
        return { hidden: !wordpressState.decided, locked: wordpressState.locked, feature: 'wordpress' };
      }
      if (id === 'analytics') {
        // Always in the nav once the package is known; locked when the base switch is off or the hub is locked.
        return { hidden: !analyticsDecided, locked: analyticsLocked, feature: 'analytics' };
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandCheckState.decided, brandCheckState.locked, wordpressState.decided, wordpressState.locked, analyticsDecided, analyticsLocked]
  );
};
